import { trackedObject } from '@ember/reactive/collections';
import Service, { type Registry as Services, service } from '@ember/service';
import { all, task } from 'ember-concurrency';
import type {
  Period,
  Restriction,
  RestrictionAction,
  RestrictionRecommendation,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { RestrictionOverridePayload } from 'lumen/services/repositories/restriction-action';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  ABSTRACT_ACTIONS,
  type MinimalRestrictionAction,
  type RestrictionActionPostPayload,
  type RestrictionContext,
} from 'lumen/utils/constants/restriction-actions';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

/**
 * Service that implements Restrictions Actions logic for create... to be continued
 */
export default class RestrictionsDataRequestServiceService extends Service {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service('repositories/restriction-action')
  declare repositoriesRestrictionAction: Services['repositories/restriction-action'];
  @service('restrictions/action-poller')
  declare restrictionsActionPoller: Services['restrictions/action-poller'];
  @service declare store: Services['store'];

  /**
   * Optional callback invoked after a push completes, to handle override-specific
   * cleanup and re-fetching. Set by callers that send an override payload
   * (e.g. overbooking modal). Cleared after each invocation.
   */
  overrideCallbackOnPushFinished:
    | ((day: string, subscriptionId: string) => Promise<void>)
    | null = null;

  pendingDates = trackedObject<Record<string, boolean>>();

  /**
   * Task to execute a POST call to BE, and create an action for a new restriction.
   * @throws
   */
  createRestrictionActionTask = task(
    { enqueue: true },
    async (
      payload: RestrictionActionPostPayload,
      override?: RestrictionOverridePayload | RestrictionOverridePayload[],
    ) => {
      try {
        return this.repositoriesRestrictionAction.add(payload, override);
      } catch (error: unknown) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  restrictionActionRequestTask = task(
    {},
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      try {
        const restrictionActions = await this.store.query<RestrictionAction>(
          'restriction-action',
          {
            namespace: APIGATEWAY,
            typeKey: 'pricing-assistant/restrictions/recommendations/action',
            from_date_range_start: rangeStart,
            from_date_range_end: rangeEnd,
            subscription_id: hotelId,
          },
        );

        return [...restrictionActions];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  restrictionsRecommendationsRequestTask = task(
    {},
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      let apiResponse = null;

      try {
        const requestParams = {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/restrictions/recommendations',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: hotelId,
        };

        const response = await this.store.query<RestrictionRecommendation>(
          'restriction-recommendation',
          requestParams,
        );
        apiResponse = [...response];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }

      return apiResponse;
    },
  );
  restrictionsRequestTask = task(
    {},
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      let apiResponse = null;

      try {
        const requestParams = {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/restrictions/aggregated',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: hotelId,
        };

        const response = await this.store.query<Period>(
          'period',
          requestParams,
        );
        apiResponse = [...response];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }

      return apiResponse;
    },
  );

  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);

    // Register local function as callback for side effect
    // SMART DISTRIBUTION TODO: Keep an eye on this setup. It's not super conventional, but was added to avoid circular dependencies
    // between restriction-action-poller, base-action-poller & restriction-data-request-service.
    // Was not worth spending more time on around the beta release. Can be revisited later
    this.restrictionsActionPoller.onActionInEndStateSubscription =
      this.restrictionsCleanupOnPushFinished.bind(this);
  }

  /**
   * Create (inexistent) restriction in API and IF needed, triggers the polling flow
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async createRestrictionAction(
    data: MinimalRestrictionAction,
    context: RestrictionContext,
    override?: RestrictionOverridePayload | RestrictionOverridePayload[],
  ) {
    const payload: RestrictionActionPostPayload = {
      ...data,
      subscription: parseInt(this.hotelAndMarketService.hotel.id!, 10),
      context,
      action: ABSTRACT_ACTIONS.ACCEPTED,
    };

    return this.createRestrictionActionTask.perform(payload, override);
  }

  /**
   * Executes new requests to update date after an action is pushed
   */
  async restrictionsCleanupOnPushFinished(
    day: string,
    subscriptionId: string,
  ): Promise<void> {
    const restrictions = this.store.peekAll<Restriction>('restriction');
    const restrictionRecommendations =
      this.store.peekAll<RestrictionRecommendation>(
        'restriction-recommendation',
      );
    const restrictionActions =
      this.store.peekAll<RestrictionAction>('restriction-action');

    restrictions
      .filter((r) => r.fromDate.format('YYYY-MM-DD') === day)
      .forEach((r) => this.store.unloadRecord(r));

    restrictionRecommendations
      .filter((r) => r.fromDate.format('YYYY-MM-DD') === day)
      .forEach((r) => this.store.unloadRecord(r));

    restrictionActions
      .filter((a) => a.fromDate.format('YYYY-MM-DD') === day)
      .forEach((a) => this.store.unloadRecord(a));

    /* Override records must be in the store before restrictions load,
       otherwise the sync hasMany resolves empty and the UI falls back to the recommendation value. */
    if (this.overrideCallbackOnPushFinished) {
      await this.overrideCallbackOnPushFinished(day, subscriptionId);
      this.overrideCallbackOnPushFinished = null;
    }

    await all([
      this.restrictionsRequestTask.perform(subscriptionId, day, day),
      this.restrictionsRecommendationsRequestTask.perform(
        subscriptionId,
        day,
        day,
      ),
      this.restrictionActionRequestTask.perform(subscriptionId, day, day),
    ]);
  }

  /**
   * Public method for pushing restriction actions to BE.
   * Once triggered the function must decide to create a new RestrictionAction,
   * or to push an existing RestrictionRecommendation.
   */
  async sendRestriction(
    data: MinimalRestrictionAction,
    context: RestrictionContext,
    override?: RestrictionOverridePayload | RestrictionOverridePayload[],
  ): Promise<void> {
    const fromDate = data.from_date;
    if (fromDate) {
      this.pendingDates[fromDate] = true;
    }
    try {
      const result = await this.createRestrictionAction(
        data,
        context,
        override,
      );

      if (result.isSuccess && result.apiResponse) {
        if (result.apiResponse.length > 0) {
          result.apiResponse.forEach((restrictionAction) => {
            this.restrictionsActionPoller.addActionsToFollow([
              restrictionAction,
            ]);
          });
        } else {
          const firstOverride = Array.isArray(override)
            ? override[0]
            : override;
          const cleanupDate = fromDate ?? firstOverride?.from_date;
          if (cleanupDate) {
            await this.restrictionsCleanupOnPushFinished(
              cleanupDate,
              this.hotelAndMarketService.hotel.id!,
            );
          }
        }
      }
    } finally {
      if (fromDate) {
        delete this.pendingDates[fromDate];
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'restrictions/data-request-service': RestrictionsDataRequestServiceService;
  }
}
